import React, { useState, useEffect, useContext, useRef } from 'react';
import { AppContext } from '../../../../RoutesWithAuth';
import axios from '../../../common/AxiosConfig';
import { Link, useNavigate, useLocation } from "react-router-dom";
import { makeStyles } from "@mui/styles";
import { Button, Grid, Checkbox, IconButton, styled } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import FormGroup from './FormGroup';
import { v2UIRoutePath } from '../../../common/commons';
import { getCreatePrivateTaskFields } from '../home/constant';
import FloatingMessage from '../../../common/FloatingMessage';

const MAX_FILE_COUNT = 5;
const MAX_FILE_SIZE = 100 * 1024 * 1024;
const ALLOWED_DOCUMENT_EXTENSIONS = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'pdf'];
const ALLOWED_FILE_TYPES = ['image/', 'video/', 'audio/'];
const FILE_ACCEPT_TYPES = '.doc,.docx,.xls,.xlsx,.ppt,.pptx,.pdf,image/*,video/*,audio/*';
const normalizeWhitespace = (value) => String(value || '').trim().replace(/\s+/g, ' ');
const trimValue = (value) => String(value || '').trim();
const getInitialFormData = () => ({
    poc_name: "",
    poc_email: "",
    title: "",
    application_name: "",
    task_description: "",
    task_owner_name: "",
    priority: "",
    category: "",
    kickoff_date: "",
    launch_date: "",
    due_date: "",
    send_questionnaire: false,
    fileNames: []
});

const useStyles = makeStyles(() => ({
    documentWrapper: {
        height: 'calc( 100% )'
    },
    createprivatetaskwrapper: {
        marginTop: '10px',
        width: '100%',
        padding: '0px 0px 35px 0px',
        //boxShadow: '0px 0px 1px 0px rgb(0 0 0 / 41%)',
        height: 'calc( 100% - 60px )',
        borderRadius: '10px',
        marginRight: '10px',
        '& .header': {
            background: '#7f9ed7',
            padding: '5px',
            color: "white",
            fontWeight: 'bold'
        },
        '& .invalid-feedback': {
            width: '100%',
            display: 'block !important',
            marginTop: '0.25rem',
            marginLeft: '0.25rem',
            fontSize: '100%',
            color: '#dc3545'
        }
    },
    wrapper: {
        padding: '25px 0px 10px 0px',
        rowGap: '10px',
        marginTop: "8px",
        position: 'relative',
        alignItems: 'center',
        display: 'flex',
        '& label': {
            textAlign: 'left'
        },
        '& .MuiInputBase-input': {
            paddingTop: 'calc(5px + 0.25rem) !important',
            paddingBottom: 'calc(5px + 0.25rem) !important'
        }
    },
    actionButton: {
        textTransform: 'initial'
    },
    uploadLabel: {
        paddingLeft: '24px'
    },
    uploadRow: {
        padding: '5px 0px 21px 19px',
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignItems: 'center'
    },
    uploadedFiles: {
        paddingLeft: '10px',
        flexBasis: '100%',
        minWidth: 0
    },
    uploadedFile: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        overflowWrap: 'anywhere'
    },
    clearIcon: {
        cursor: 'pointer',
        color: '#dc3545'
    },
    uploadButton: {
        backgroundColor: '#8CC0E0',
        color: 'black',
        borderRadius: '10px',
        '&:hover': {
            background: '#5BA5D3',
        },
    },
    uploadHelpText: {
        paddingTop: '13px',
        paddingLeft: '10px'
    },
    questionnaireRow: {
        paddingLeft: '25px',
        //paddingBottom: '5px'
    },
    formActions: {
        display: 'flex',
        maxWidth: '500px',
        margin: '15px auto 0px',
        paddingLeft: '55px'
    },
    cancelButton: {
        backgroundColor: '#3924FC',
        color: '#FFFFFF',
        marginRight: '100px',
        width: '8.5vw',
        borderRadius: '8px'
    },
    submitButton: {
        backgroundColor: '#3924FC',
        color: '#FFFFFF',
        width: '8.5vw',
        borderRadius: '8px'
    },
    sidebarDivider: {
        borderTop: '1px solid #eee3e3',
        width: '100vw',
    }

}));

const VisuallyHiddenInput = styled('input')({
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

function CreatePrivacyTask() {
    const navigate = useNavigate();
    // const location = useLocation();
    const classes = useStyles()
    const [users, setUsers] = useState([]);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');
    const [maxLengthMessage, setMaxLengthMessage] = useState('');
    const [submitting, setSubmitting] = useState(false);
    //const [verticalTab, setVerticalTab] = useState(0);
    const [formData, setFormData] = useState(getInitialFormData);
    const formGroupRef = useRef(null);

    const context = useContext(AppContext)
    const authToken = context.authToken.get

    useEffect(() => {
        fetchData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authToken])

    const updateFormData = (updates) => {
        setFormData((current) => ({ ...current, ...updates }));
    }

    const handleCancel = () => {
        formGroupRef.current.resetFormToDefaults();
        setFormData(getInitialFormData());
        setError(null);
        setSuccessMessage('');
        setMaxLengthMessage('');
    }

    const handleFieldUpdates = (formInfo) => {
        const taskOwner = users.find(({ user_id }) => user_id === formInfo.task_owner);
        updateFormData({
            poc_name: formInfo.first_and_last_name || "",
            poc_email: formInfo.email || "",
            title: formInfo.title || "",
            application_name: formInfo.application_or_system || "",
            task_description: formInfo.task_description || "",
            task_owner_name: taskOwner ? `${taskOwner.first_name} ${taskOwner.last_name}` : "",
            priority: formInfo.priority || "",
            category: formInfo.task_category || "",
            kickoff_date: formInfo.project_kickoff_date || "",
            launch_date: formInfo.launch_date || "",
            due_date: formInfo.task_due_date || ""
        });
    }

    const handleMaxLengthReached = (fieldName) => {
        const privacyTaskFieldNames = {
            first_and_last_name: 'First and Last Name',
            title: 'Title',
            application_or_system: 'Application Name'
        };
        const privacyTaskFieldName = privacyTaskFieldNames[fieldName];

        if (privacyTaskFieldName) {
            setMaxLengthMessage(`${privacyTaskFieldName} cannot exceed 200 characters`);
        }
    }

    const handleFileUpload = (event) => {
        const files = Array.from(event.target.files || []);
        const selectedFiles = files.map(file => ({ fileName: file.name, fileObject: file }));
        const nextFileNames = [...formData.fileNames, ...selectedFiles];

        if (nextFileNames.length > MAX_FILE_COUNT) {
            setError({ errorMessage: `You can upload a maximum of ${MAX_FILE_COUNT} files.` });
            event.target.value = '';
            return;
        }

        const oversizedFiles = files.filter(({ size }) => size > MAX_FILE_SIZE);
        if (oversizedFiles.length) {
            setError({ errorMessage: `Each file must be 100MB or smaller: ${oversizedFiles.map(({ name }) => name).join(', ')}.` });
            event.target.value = '';
            return;
        }

        const unsupportedFiles = files.filter((file) => {
            const extension = file.name.split('.').pop()?.toLowerCase();
            return !ALLOWED_DOCUMENT_EXTENSIONS.includes(extension) &&
                !ALLOWED_FILE_TYPES.some((type) => file.type.startsWith(type));
        });
        if (unsupportedFiles.length) {
            setError({ errorMessage: `Unsupported file type: ${unsupportedFiles.map(({ name }) => name).join(', ')}.` });
            event.target.value = '';
            return;
        }
        setError(null);
        updateFormData({ fileNames: nextFileNames });
        event.target.value = '';
    }

    const handleQuestionnaireChange = (event) => {
        updateFormData({ send_questionnaire: event.target.checked });
    }

    const handleFileRemove = (index) => {
        setFormData((current) => ({
            ...current,
            fileNames: current.fileNames.filter((_, fileIndex) => fileIndex !== index)
        }));
    }

    const handleSubmit = () => {
        const normalizedFormData = {
            ...formData,
            poc_name: normalizeWhitespace(formData.poc_name),
            title: trimValue(formData.title),
            application_name: trimValue(formData.application_name)
        };
        const requiredFields = [
            ['poc_name', 'First and Last Name'],
            ['poc_email', 'Email'],
            ['title', 'Title'],
            ['application_name', 'Application or System'],
            ['task_description', 'Task Description'],
            ['task_owner_name', 'Task Owner'],
            ['priority', 'Priority'],
            ['category', 'Task Category'],
            ['due_date', 'Task Due Date']
        ];
        const missingFields = requiredFields
            .filter(([name]) => !String(normalizedFormData[name] || '').trim())
            .map(([, label]) => label);

        if (missingFields.length) {
            setError({ errorMessage: `Please complete the mandatory fields: ${missingFields.join(', ')}.` });
            return;
        }

        if (formData.fileNames.length > MAX_FILE_COUNT) {
            setError({ errorMessage: `You can upload a maximum of ${MAX_FILE_COUNT} files.` });
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
        if (!emailRegex.test(formData.poc_email.trim())) {
            setError({ errorMessage: 'Please enter a valid Email ID.' });
            return;
        }

        const getWordCount = (value) => value.trim() ? value.trim().split(/\s+/).length : 0;
        const fieldsOverWordLimit = [
            ['poc_name', 'First and Last Name'],
            ['title', 'Title'],
            ['application_name', 'Application or System']
        ].filter(([name]) => getWordCount(normalizedFormData[name]) > 200);
        if (fieldsOverWordLimit.length) {
            setError({ errorMessage: `${fieldsOverWordLimit.map(([, label]) => label).join(' and ')} must not exceed 200 words.` });
            return;
        }

        const today = new Date();
        const todayDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
        const invalidFutureDates = [
            ['launch_date', 'Launch Date'],
            ['due_date', 'Task Due Date']
        ].filter(([name]) => formData[name] && formData[name] <= todayDate);
        if (invalidFutureDates.length) {
            setError({ errorMessage: `${invalidFutureDates.map(([, label]) => label).join(' and ')} must be a future date.` });
            return;
        }

        const trimSingleWord = (value) => getWordCount(value) === 1 ? value.trim() : value;
        const submissionData = {
            ...normalizedFormData,
            poc_name: trimSingleWord(normalizedFormData.poc_name),
            title: trimSingleWord(normalizedFormData.title),
            application_name: trimSingleWord(normalizedFormData.application_name),
            fileNames: normalizedFormData.fileNames.map(({ fileName }) => ({ fileName }))
        };

        setSubmitting(true);
        setError(null);
        let createdTaskId;
        axios.post('/centra/privacy-task', submissionData, {
            headers: {
                Authorization: authToken
            }
        }).then(async (response) => {
            createdTaskId = response.data?.task_id;
            const uploadForms = response.data?.upload_forms;
            const selectedFiles = normalizedFormData.fileNames;
            if (selectedFiles.length && !Array.isArray(uploadForms)) {
                throw new Error('The task was created, but the server did not return document upload instructions.');
            }

            const uploadedFileIndexes = new Set();
            for (const uploadForm of uploadForms || []) {
                const fileIndex = selectedFiles.findIndex((file, index) =>
                    !uploadedFileIndexes.has(index) && file.fileName === uploadForm.file_name
                );
                if (fileIndex === -1) {
                    throw new Error(`The server returned upload instructions for an unexpected document: ${uploadForm.file_name || 'unknown file'}.`);
                }
                if (!uploadForm.upload?.url || !uploadForm.upload?.fields || !selectedFiles[fileIndex].fileObject) {
                    throw new Error(`The server returned incomplete upload instructions for ${uploadForm.file_name}.`);
                }

                const uploadData = new FormData();
                Object.entries(uploadForm.upload.fields).forEach(([key, value]) => {
                    uploadData.append(key, value);
                });
                uploadData.append('file', selectedFiles[fileIndex].fileObject);
                await axios.post(uploadForm.upload.url, uploadData);
                uploadedFileIndexes.add(fileIndex);
            }

            if (uploadedFileIndexes.size !== selectedFiles.length) {
                const missingFile = selectedFiles.find((_, index) => !uploadedFileIndexes.has(index));
                throw new Error(`The server did not return upload instructions for ${missingFile.fileName}.`);
            }

            setSuccessMessage(response.data?.message || 'Privacy Task created successfully.');
        }).catch(err => {
            setError({
                errorMessage: createdTaskId
                    ? `Privacy Task ${createdTaskId} was created, but document upload failed: ${err?.response?.data?.message || err?.message || 'Unknown upload error.'}`
                    : err?.response?.data?.message || err?.message || 'Something went wrong while creating the privacy task.'
            });
        }).finally(() => {
            setSubmitting(false);
        })
    }

    const fetchData = () => {
        // Get the list of all users
        axios.get('users', {
            headers: {
                Authorization: authToken
            }
        }).then(res => {
            if (res.data.length) {
                const filteredUsers = res.data.filter(user => user.user_status === 'Y' && !user.is_disabled);
                //setAssignedTo(userId)
                setUsers(filteredUsers)
            }
        }).catch(err => {
            console.log('There was an error fetching users!', err)
        })
    }
    //const isAuto = verticalTab === 0;

    return (
        <div className={classes.documentWrapper}>
            <div className={classes.createprivatetaskwrapper}>
                <FormGroup
                    ref={formGroupRef}
                    disableFormActions
                    formWrapperClassName={classes.wrapper}
                    fields={getCreatePrivateTaskFields(users)}
                    customFormUpdates={handleFieldUpdates}
                    onMaxLengthReached={handleMaxLengthReached}
                />
                <span className={classes.uploadLabel}>Upload Documents</span>
                <div className={classes.uploadRow}>
                    <Button
                        className={`${classes.actionButton} ${classes.uploadButton}`}
                        component="label"
                        role={undefined}
                        variant="contained"
                        tabIndex={-1}
                    >
                        Upload File
                        <VisuallyHiddenInput
                            type="file"
                            aria-label="Upload File"
                            accept={FILE_ACCEPT_TYPES}
                            onChange={handleFileUpload}
                            multiple
                        />
                    </Button>
                    <p className={classes.uploadHelpText}>File number limit: 5. Single file size limit: 100 MB each file. Allowed file types: Word, Excel, PPT, PDF, Image, Video, Audio</p>
                    {formData.fileNames.length > 0 && (
                        <div className={classes.uploadedFiles}>
                            {formData.fileNames.map(({ fileName }, index) => (
                                <div key={index} className={classes.uploadedFile}>
                                    <span>{fileName}</span>
                                    <IconButton
                                        size="small"
                                        aria-label={`Remove ${fileName}`}
                                        onClick={() => handleFileRemove(index)}
                                    >
                                        <ClearIcon className={classes.clearIcon}fontSize="small" />
                                    </IconButton>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                <div className={classes.questionnaireRow}>
                    <Checkbox
                        checked={formData.send_questionnaire}
                        onChange={handleQuestionnaireChange}
                    />
                    <span>Send Questionnaire to point of contact</span>
                </div>
                <Grid item xs={10} alignContent={"center"} className={classes.formActions}>
                    <Button
                        className={`${classes.actionButton} ${classes.cancelButton}`}
                        variant="contained"
                        color="primary"
                        onClick={handleCancel}
                    >
                        Cancel
                    </Button>
                    <Button
                        className={`${classes.actionButton} ${classes.submitButton}`}
                        variant="contained"
                        color="primary"
                        disabled={submitting}
                        onClick={handleSubmit}
                    >
                        Submit
                    </Button>
                </Grid>
                {error && (
                    <>
                        <div className="invalid-feedback" id="error-message">
                            {error.errorMessage}
                        </div>
                        {error.id && (<Link to={`${v2UIRoutePath}requestdetails/${error.id}`}>View Existing Ticket</Link>)}
                    </>
                )}
                <FloatingMessage
                    open={successMessage !== ''}
                    message={successMessage}
                    severity="success"
                    onClose={() => {
                        setSuccessMessage('');
                        navigate(`${v2UIRoutePath}centra/privacyTasks`);
                    }}
                />
                <FloatingMessage
                    open={maxLengthMessage !== ''}
                    message={maxLengthMessage}
                    onClose={() => setMaxLengthMessage('')}
                />
            </div>
        </div>
    );
}
export default CreatePrivacyTask;